/**
 * Blocks mixing "Old Is Gold" clearance products with regular products in the same cart, in
 * either direction, with an explanatory message -- matching how clearance-sale invoicing already
 * works (see the Old Is Gold terms: clearance line items are marked separately on the invoice).
 *
 * Every add-to-cart entry point in this theme (main product page, quick-buy popover/drawer,
 * product-item quick-buy cards, featured-product section) ultimately submits a FormData POST to
 * /cart/add.js via fetch() (see assets/theme.js ProductForm._onSubmit). Rather than patch that
 * class directly or attach a listener to every individual <form> (fragile if a new entry point is
 * added later), this patches window.fetch globally and inspects any /cart/add request -- the same
 * technique the store's own "Restock Rocket" app already uses for its own cart interception
 * (visible in the console as "[RR] Fetch request modified: /cart/add.js"), so it's a proven-safe
 * pattern for this storefront.
 *
 * Old Is Gold products are identified via a hidden line-item property, properties[_old_is_gold]
 * (added in snippets/product-form.liquid and snippets/product-item.liquid for any product tagged
 * "old-is-gold"). The leading underscore means Shopify/this theme's cart UI never displays it to
 * the customer (see sections/mini-cart.liquid's property loop). That same property round-trips
 * into /cart.js's line item data, so it doubles as the way to detect what's already in the cart.
 */
(() => {
  const BLOCK_MESSAGE_ADDING_OIG = "Old Is Gold clearance items must be purchased separately from regular items. Please complete your current order first, or remove the other item to continue.";
  const BLOCK_MESSAGE_ADDING_REGULAR = "Your cart contains an Old Is Gold clearance item, which must be purchased separately. Please complete that order first, or remove it before adding regular items.";

  function bodyIndicatesOldIsGold(body) {
    if (!body) return false;
    if (body instanceof FormData) {
      return body.has('properties[_old_is_gold]');
    }
    if (body instanceof URLSearchParams) {
      return body.has('properties[_old_is_gold]');
    }
    if (typeof body === 'string') {
      return body.indexOf('_old_is_gold') !== -1;
    }
    return false;
  }

  function summarizeCart(cart) {
    let hasOldIsGold = false;
    let hasRegular = false;
    (cart.items || []).forEach((item) => {
      if (item.properties && item.properties._old_is_gold) {
        hasOldIsGold = true;
      } else {
        hasRegular = true;
      }
    });
    return { hasOldIsGold, hasRegular };
  }

  function blockedResponse(message) {
    return new Response(JSON.stringify({ status: 422, message: 'Unprocessable Entity', description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Normally the theme's own <form is="product-form"> submit handler is what
  // notices a non-ok /cart/add response and dispatches "cart-notification:show"
  // to surface it. But this storefront's "Restock Rocket" app takes over the
  // add-to-cart button's click before it ever becomes a real form submission
  // (it does its own fetch to /cart/add.js directly) -- so that theme code path
  // never runs, and a block from this guard happened silently with no message,
  // even though the block itself worked (this guard intercepts at the fetch
  // level, which fires regardless of what triggered it). Dispatching the same
  // event ourselves, right here, means the message shows no matter which UI
  // component initiated the add.
  function announceBlocked(message) {
    document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: { status: 'error', error: message }
    }));
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function cyanOldIsGoldGuardedFetch(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = ((init && init.method) || (typeof input === 'object' && input.method) || 'GET').toUpperCase();

    if (method === 'POST' && url.indexOf('/cart/add') !== -1) {
      const addingOldIsGold = bodyIndicatesOldIsGold(init && init.body);

      try {
        const cartRes = await originalFetch('/cart.js');
        const cart = await cartRes.json();
        const { hasOldIsGold, hasRegular } = summarizeCart(cart);

        const blocked = (addingOldIsGold && hasRegular) || (!addingOldIsGold && hasOldIsGold);
        if (blocked) {
          const message = addingOldIsGold ? BLOCK_MESSAGE_ADDING_OIG : BLOCK_MESSAGE_ADDING_REGULAR;
          announceBlocked(message);
          return blockedResponse(message);
        }
      } catch (error) {
        // If the cart-state check itself fails for any reason, fail open (let the add proceed)
        // rather than silently blocking real purchases due to an unrelated network hiccup.
      }
    }

    return originalFetch(input, init);
  };
})();
